import type { ReactNode } from 'react';
import { AuthProvider as LegacyAuthProvider } from '@/components/AuthProvider';
/*
 * From the module that defines it, NOT from the barrel.
 *
 * `@/components/shared/SearchModal` re-exports the provider AND the modal, so
 * importing one pulls the other -- which made the lazy() below do nothing at all.
 * The modal was still in the eager graph, arriving by a different door.
 *
 * A barrel is a convenience for a call site and a liability for a bundler: it
 * turns "I need this one thing" into "I need everything this folder exports".
 */
import { SearchModalProvider } from '@/components/shared/SearchModal/SearchModalContext';
import { ThemeProvider as LegacyThemeProvider } from '@/components/ThemeProvider';
import { CollectionsProvider } from '@/contexts/CollectionsContext';
import { ToastEventListener, ToastProvider } from '@/components/ui/toast';
import { AuthProvider } from '@/features/auth/session';

import { AppToaster } from './AppToaster';
import { ErrorBoundary } from './ErrorBoundary';
import { QueryProvider } from './QueryProvider';
import { EnhancedTooltipProvider } from '@/components/ui/enhanced-tooltip';
import { SearchModalMount } from './SearchModalMount';

/**
 * Everything the application needs wrapped around it, in one place.
 *
 * <p>Order matters: the boundary is outermost, so a provider that throws while
 * initialising is caught rather than taking the page down before anything can
 * render a message.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      {/*
          The providers the ported components expect, in the order the
          application they came from mounted them. Theirs and ours run side by
          side deliberately: the copied tree reads its own contexts, and nothing
          written here has to be rewritten to talk to them.

          Collapsing them is the refactor. Doing it while the markup is still
          being brought across would mean changing the components at the same time
          as moving them, which is how you lose track of which half broke
          something.

          ONE EXCEPTION, and it had to be made: shared/theme is NOT mounted.
          Both providers write `dark` on documentElement, so with both in the tree
          they fight over one class -- the toggle stored the new theme and the page
          never changed, which reads as a broken control rather than a conflict.
          Two owners of one piece of DOM is not a thing to defer.

          It keeps its tests and stays the target of the collapse; it simply is not
          in charge while the ported provider is.
        */}
      <LegacyThemeProvider>
        <ToastProvider>
          {/*
            The provider holds the queue; this renders it. `toast.success(...)`
            dispatches a `show-toast` window event, and with nothing listening the
            call succeeds, the event fires and nothing appears -- which is how
            "Dark theme applied" was missing while the theme itself worked.
          */}
          <ToastEventListener />
          <SearchModalProvider>
            {/*
              Same shape as the toast listener: the provider holds the state, this
              renders it. The landing page's Search button calls
              `startBrandAnalysis`, which without this updates a context nothing is
              displaying -- the click appears to do nothing at all.
            */}
            <SearchModalMount />
            <LegacyAuthProvider>
              <QueryProvider>
                {/* Inside QueryProvider, because the session is fetched rather
                      than read out of the token -- access tokens here are opaque
                      references and carry nothing about the person. */}
                <AuthProvider>
                  {/* The Brand Analyser's save-to-collection state, mounted where the
                      application it came from mounted it. */}
                  <CollectionsProvider>
                    {/* Radix refuses a tooltip with no provider above it. The copied screens
                        (Brand Overview's status tooltips) assume one, because the application
                        they came from mounted it in its layout. */}
                    <EnhancedTooltipProvider>{children}</EnhancedTooltipProvider>
                    <AppToaster />
                  </CollectionsProvider>
                </AuthProvider>
              </QueryProvider>
            </LegacyAuthProvider>
          </SearchModalProvider>
        </ToastProvider>
      </LegacyThemeProvider>
    </ErrorBoundary>
  );
}
